import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, Sliders, Map as MapIcon, FileText, Cpu, Landmark, RefreshCw, ExternalLink, Languages, Download, Upload, Tag, Trash2, Plus } from "lucide-react";
import Header from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPricing() {
  const nav = useNavigate();
  const [token] = useState(localStorage.getItem("dz_admin_token"));
  const [tab, setTab] = useState("components");
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [calcOverrides, setCalcOverrides] = useState({});
  const [regionDrafts, setRegionDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [financing, setFinancing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromo, setNewPromo] = useState({
    code: "", discount_type: "percent", discount_value: "", label: "",
    max_uses: "", expires_at: "", active: true,
  });

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    load();
    loadFinancing();
    loadPromo();
  }, [token]);

  const loadPromo = async () => {
    try {
      const r = await axios.get(`${API}/admin/promo`,
        { headers: { Authorization: `Bearer ${token}` } });
      setPromoCodes(r.data.codes || []);
    } catch { /* ignore */ }
  };

  const savePromo = async () => {
    if (!newPromo.code.trim() || !newPromo.discount_value) {
      toast.error("Código y valor obligatorios");
      return;
    }
    try {
      const payload = {
        ...newPromo,
        discount_value: Number(newPromo.discount_value),
        max_uses: newPromo.max_uses ? Number(newPromo.max_uses) : null,
        min_amount_eur: null,
        expires_at: newPromo.expires_at || null,
      };
      await axios.post(`${API}/admin/promo`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Código guardado");
      setNewPromo({ code: "", discount_type: "percent", discount_value: "", label: "",
                    max_uses: "", expires_at: "", active: true });
      loadPromo();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || "guardando"));
    }
  };

  const togglePromo = async (code, active) => {
    try {
      const existing = promoCodes.find((p) => p.code === code);
      await axios.post(`${API}/admin/promo`, { ...existing, active },
        { headers: { Authorization: `Bearer ${token}` } });
      loadPromo();
    } catch { toast.error("Error"); }
  };

  const deletePromo = async (code) => {
    if (!window.confirm(`¿Borrar código ${code}?`)) return;
    await axios.delete(`${API}/admin/promo/${code}`,
      { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Borrado");
    loadPromo();
  };

  const loadFinancing = async () => {
    try {
      const r = await axios.get(`${API}/admin/financing`,
        { headers: { Authorization: `Bearer ${token}` } });
      setFinancing(r.data);
      setRefreshing(!!r.data.running);
    } catch { /* ignore */ }
  };

  const refreshFinancing = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/financing/refresh`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Refresco iniciado · puede tardar 1-2 min");
      // poll every 5s until done
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/admin/financing`,
            { headers: { Authorization: `Bearer ${token}` } });
          setFinancing(r.data);
          if (!r.data.running) {
            clearInterval(poll);
            setRefreshing(false);
            toast.success("Información de bancos actualizada");
          }
        } catch { clearInterval(poll); setRefreshing(false); }
      }, 5000);
    } catch (e) {
      toast.error("Error al iniciar refresco");
      setRefreshing(false);
    }
  };

  const downloadTranslations = async () => {
    try {
      const r = await axios.get(`${API}/admin/translations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "doctorsol_translations.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) { toast.error("Error descargando Excel"); }
  };

  const uploadTranslations = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/translations/import`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const counts = Object.entries(r.data.updated_keys || {})
        .map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`Vertalingen bijgewerkt — ${counts}`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || "fout bij importeren"));
    } finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/pricing`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
      setOverrides(r.data.overrides || {});
      setCalcOverrides(r.data.calc_overrides || {});
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("dz_admin_token"); nav("/admin"); }
      else toast.error("Error cargando datos");
    }
  };

  const saveComponents = async () => {
    setBusy(true);
    try {
      const numOverrides = {};
      Object.entries(overrides).forEach(([k, v]) => {
        if (k.startsWith("regions.")) return;
        const n = Number(v);
        if (!isNaN(n) && n > 0) numOverrides[k] = n;
      });
      await axios.put(`${API}/admin/pricing`, { overrides: numOverrides },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Precios guardados");
      load();
    } catch (e) { toast.error("Error guardando"); }
    finally { setBusy(false); }
  };

  const saveCalc = async () => {
    setBusy(true);
    try {
      const numCalc = {};
      Object.entries(calcOverrides).forEach(([k, v]) => {
        const n = Number(v);
        if (!isNaN(n) && n > 0) numCalc[k] = n;
      });
      await axios.put(`${API}/admin/pricing`, { calc_overrides: numCalc },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Motor de cálculo actualizado");
      load();
    } catch (e) { toast.error("Error guardando"); }
    finally { setBusy(false); }
  };

  const saveRegion = async (regionId) => {
    const draft = regionDrafts[regionId] || {};
    if (Object.keys(draft).length === 0) return;
    setBusy(true);
    try {
      const payload = {};
      Object.entries(draft).forEach(([k, v]) => {
        const n = Number(v);
        if (!isNaN(n) && n >= 0) payload[k] = n;
      });
      await axios.put(`${API}/admin/regions/${regionId}`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${regionId} actualizado`);
      setRegionDrafts(prev => ({ ...prev, [regionId]: {} }));
      load();
    } catch (e) { toast.error("Error guardando"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#FFFFFF]"><Header minimal /><div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#E78938]" /></div></div>;

  const fields = data.fields || [];

  return (
    <div className="min-h-screen bg-white" data-testid="admin-pricing">
      <Header minimal />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <button onClick={() => nav("/admin/dashboard")}
          className="text-sm text-[#475569] hover:text-[#E78938] mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Volver al dashboard
        </button>
        <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] font-semibold mb-1">Configuración</div>
        <h1 className="serif text-4xl text-[#0F172A] mb-2">Precios del motor de cálculo</h1>
        <p className="text-[#475569] mb-8">Define tus precios — sobreescriben los valores por defecto del mercado español 2026 en todos los nuevos rapports generados.</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#E2E8F0]" data-testid="pricing-tabs">
          {[
            ["components", "Componentes", Sliders],
            ["calc", "Motor de cálculo", Cpu],
            ["regions", "Tarifas regionales", MapIcon],
            ["financing", "Financiación", Landmark],
            ["promo", "Promo codes", Tag],
            ["translations", "Vertalingen", Languages],
          ].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-5 py-3 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition ${tab === k ? "border-[#E78938] text-[#E78938]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}>
              <Ic className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {tab === "components" && (
          <div data-testid="tab-components-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF7ED] text-[#E78938] uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Componente</th>
                    <th className="text-right px-5 py-3">Por defecto</th>
                    <th className="text-right px-5 py-3">Tu precio</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map(([key, label, unit]) => (
                    <tr key={key} className="border-t border-[#E2E8F0]">
                      <td className="px-5 py-3 text-[#0F172A]">{label}</td>
                      <td className="px-5 py-3 text-right text-[#94A3B8] tnum">{data.defaults[key]?.toLocaleString()} <span className="text-xs">{unit}</span></td>
                      <td className="px-5 py-3 text-right">
                        <input type="number" min="0" step="1"
                          data-testid={`price-${key}`}
                          placeholder={data.defaults[key]}
                          value={overrides[key] ?? ""}
                          onChange={(e) => setOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-32 bg-[#FFF7ED] border border-transparent focus:border-[#E78938]/40 rounded-lg px-3 py-1.5 text-right tnum outline-none" />
                        <span className="text-xs text-[#94A3B8] ml-2">{unit}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="save-components" disabled={busy} onClick={saveComponents}
                className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar cambios
              </button>
            </div>
          </div>
        )}

        {tab === "calc" && (
          <div data-testid="tab-calc-panel">
            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-5 mb-6 text-sm text-[#475569]">
              <strong className="text-[#0F172A]">Motor de cálculo</strong> — Estos parámetros gobiernan la calculadora de la landing y los cálculos de ROI del informe. Modifícalos para recalibrar los resultados a tu mercado real.
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF7ED] text-[#E78938] uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Variable</th>
                    <th className="text-right px-5 py-3">Por defecto</th>
                    <th className="text-right px-5 py-3">Tu valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.calc_fields || []).map(([key, label, unit, step]) => (
                    <tr key={key} className="border-t border-[#E2E8F0]">
                      <td className="px-5 py-3 text-[#0F172A]">{label}</td>
                      <td className="px-5 py-3 text-right text-[#94A3B8] tnum">{data.calc_defaults?.[key]} <span className="text-xs">{unit}</span></td>
                      <td className="px-5 py-3 text-right">
                        <input type="number" min="0" step={step}
                          data-testid={`calc-${key}`}
                          placeholder={data.calc_defaults?.[key]}
                          value={calcOverrides[key] ?? ""}
                          onChange={(e) => setCalcOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-32 bg-[#FFF7ED] border border-transparent focus:border-[#E78938]/40 rounded-lg px-3 py-1.5 text-right tnum outline-none" />
                        <span className="text-xs text-[#94A3B8] ml-2">{unit}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="save-calc" disabled={busy} onClick={saveCalc}
                className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar motor
              </button>
            </div>
          </div>
        )}

        {tab === "regions" && (
          <div className="space-y-4" data-testid="tab-regions-panel">
            {(data.regions || []).map((r) => {
              const draft = regionDrafts[r.id] || {};
              const v = (k) => draft[k] !== undefined ? draft[k] : r[k];
              const setV = (k, val) => setRegionDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), [k]: val } }));
              return (
                <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid={`region-${r.id}`}>
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="serif text-2xl text-[#0F172A]">{r.name}</h3>
                    <span className="text-xs text-[#94A3B8] mono">{r.id}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      ["kwh_price", "Precio kWh (€)", "0.001"],
                      ["sun_hours", "Horas de sol/día", "0.1"],
                      ["irpf_pct", "IRPF deducción (0-1)", "0.01"],
                      ["ibi_pct", "IBI bonificación (0-1)", "0.01"],
                      ["ibi_years", "IBI años", "1"],
                      ["eu_grant_eur_per_kwp", "EU subv. €/kWp", "10"],
                    ].map(([k, l, step]) => (
                      <label key={k} className="block">
                        <div className="text-xs text-[#94A3B8] mb-1">{l}</div>
                        <input type="number" step={step}
                          data-testid={`region-${r.id}-${k}`}
                          value={v(k) ?? ""}
                          onChange={(e) => setV(k, e.target.value)}
                          className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#E78938]/30" />
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end mt-4">
                    <button onClick={() => saveRegion(r.id)} disabled={busy}
                      data-testid={`save-region-${r.id}`}
                      className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Guardar {r.name}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {tab === "financing" && (
          <div data-testid="tab-financing-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-5 text-sm text-[#475569]">
                  <strong className="text-[#0F172A]">Catálogo de financiación</strong> — {(financing?.catalog || []).length} entidades incluidas en cada dossier (sección 19). Pulsa <em>Refrescar</em> para que la IA visite cada web y actualice descripciones, importes máximos y plazos automáticamente.
                </div>
                {financing?.last_refresh && (
                  <div className="text-xs text-[#94A3B8] mt-2 mono">
                    Último refresco: {new Date(financing.last_refresh.finished_at).toLocaleString("es-ES")} · OK: {financing.last_refresh.ok}/{financing.last_refresh.total} · {financing.last_refresh.duration_s?.toFixed(0)}s
                  </div>
                )}
              </div>
              <button onClick={refreshFinancing} disabled={refreshing}
                data-testid="refresh-financing"
                className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait">
                {refreshing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Refrescando bancos…</>
                  : <><RefreshCw className="w-4 h-4" /> Refrescar info bancos</>}
              </button>
            </div>
            <div className="space-y-3">
              {(financing?.catalog || []).map((entry) => (
                <div key={entry.name} className="bg-white border border-[#E2E8F0] rounded-xl p-5"
                     data-testid={`financing-${entry.name}`}>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-2">
                    <div>
                      <h3 className="serif text-xl text-[#0F172A]">{entry.name}</h3>
                      <div className="text-xs text-[#7D927E] mt-0.5">{entry.category} · <em>{entry.product}</em></div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {entry.confidence && (
                        <span className={`px-2 py-1 rounded-full font-semibold ${
                          entry.confidence === "high" ? "bg-[#DCFCE7] text-[#16A34A]" :
                          entry.confidence === "medium" ? "bg-[#FEF3C7] text-[#B45309]" :
                          "bg-[#FEE2E2] text-[#B91C1C]"
                        }`}>
                          {entry.via === "scrape" ? "Web ✓" : entry.via === "knowledge" ? "IA·base" : "IA"}: {entry.confidence}
                        </span>
                      )}
                      <a href={entry.url} target="_blank" rel="noopener noreferrer"
                         className="text-[#C55928] hover:underline inline-flex items-center gap-1">
                        Visitar <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed">{entry.description}</p>
                  <ul className="mt-2 space-y-1 text-xs text-[#0F172A]">
                    {(entry.highlights || []).map((h, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#E78938]">•</span>{h}
                      </li>
                    ))}
                  </ul>
                  {entry.refreshed_at && (
                    <div className="text-[10px] text-[#94A3B8] mt-2 mono">
                      Actualizado: {new Date(entry.refreshed_at).toLocaleString("es-ES")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "promo" && (
          <div data-testid="tab-promo-panel">
            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-5 mb-6 text-sm text-[#475569]">
              <strong className="text-[#0F172A]">Promo codes</strong> — Crea descuentos para campañas en redes sociales. Los códigos se introducen en el portal antes del pago.
            </div>

            {/* New code form */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-6">
              <h3 className="serif text-xl text-[#0F172A] mb-4 flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#E78938]" /> Crear código
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Código</label>
                  <input data-testid="promo-code-input"
                    value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                    placeholder="DOCTORSOL10"
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 mono uppercase outline-none focus:ring-2 focus:ring-[#E78938]/40" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Etiqueta interna</label>
                  <input value={newPromo.label} onChange={(e) => setNewPromo({ ...newPromo, label: e.target.value })}
                    placeholder="Campaña Instagram mayo"
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#E78938]/40" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Tipo de descuento</label>
                  <select value={newPromo.discount_type} onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 outline-none">
                    <option value="percent">% porcentaje</option>
                    <option value="amount_eur">€ importe fijo</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Valor</label>
                  <input type="number" min="0.01" step="0.01"
                    value={newPromo.discount_value} onChange={(e) => setNewPromo({ ...newPromo, discount_value: e.target.value })}
                    placeholder={newPromo.discount_type === "percent" ? "10" : "20"}
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 tnum outline-none focus:ring-2 focus:ring-[#E78938]/40" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Usos máximos (vacío = ilimitado)</label>
                  <input type="number" min="1"
                    value={newPromo.max_uses} onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 tnum outline-none focus:ring-2 focus:ring-[#E78938]/40" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Caducidad</label>
                  <input type="date"
                    value={newPromo.expires_at?.slice(0, 10) || ""}
                    onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
                    className="w-full bg-[#FFF7ED] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#E78938]/40" />
                </div>
              </div>
              <button onClick={savePromo} data-testid="save-promo"
                className="mt-4 bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-5 py-2.5 inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> Guardar código
              </button>
            </div>

            {/* Existing codes table */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF7ED] text-[#E78938] uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Código</th>
                    <th className="text-left px-5 py-3">Descuento</th>
                    <th className="text-left px-5 py-3">Etiqueta</th>
                    <th className="text-right px-5 py-3">Usos</th>
                    <th className="text-left px-5 py-3">Caduca</th>
                    <th className="text-center px-5 py-3">Activo</th>
                    <th className="text-right px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.length === 0 ? (
                    <tr><td colSpan="7" className="px-5 py-8 text-center text-[#94A3B8]">Aún no hay códigos. Crea el primero arriba.</td></tr>
                  ) : promoCodes.map((p) => (
                    <tr key={p.code} className="border-t border-[#E2E8F0]">
                      <td className="px-5 py-3 mono uppercase font-semibold text-[#0F172A]">{p.code}</td>
                      <td className="px-5 py-3 tnum">
                        {p.discount_type === "percent"
                          ? `${p.discount_value}%`
                          : `€${p.discount_value}`}
                      </td>
                      <td className="px-5 py-3 text-[#475569]">{p.label || "—"}</td>
                      <td className="px-5 py-3 text-right tnum">
                        {p.uses_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}
                      </td>
                      <td className="px-5 py-3 text-[#475569]">
                        {p.expires_at ? new Date(p.expires_at).toLocaleDateString("es-ES") : "—"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button onClick={() => togglePromo(p.code, !p.active)}
                          className={`w-10 h-5 rounded-full transition ${p.active ? "bg-[#E78938]" : "bg-[#FED7AA]"}`}>
                          <div className={`w-4 h-4 rounded-full bg-white transition-all ${p.active ? "translate-x-5" : "translate-x-0.5"}`} />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => deletePromo(p.code)}
                          data-testid={`delete-${p.code}`}
                          className="text-[#94A3B8] hover:text-[#C55928]">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "translations" && (
          <div data-testid="tab-translations-panel">
            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-5 mb-6 text-sm text-[#475569]">
              <strong className="text-[#0F172A]">Vertalingen exporteren / importeren</strong> — Download de Excel met alle UI-teksten in 7 talen, pas aan in Excel/Numbers/Google Sheets en upload terug. ES is de bron en blijft ongewijzigd; lege cellen blijven onveranderd.
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-7 text-center">
                <Download className="w-8 h-8 text-[#E78938] mx-auto mb-3" strokeWidth={1.8} />
                <h3 className="serif text-2xl text-[#0F172A] mb-2">Exporteren</h3>
                <p className="text-sm text-[#64748B] mb-5">Download een Excel-bestand met alle teksten netjes per sectie en taal.</p>
                <button onClick={downloadTranslations} data-testid="export-translations"
                  className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-5 py-3 inline-flex items-center gap-2">
                  <Download className="w-4 h-4" /> Excel downloaden
                </button>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-7 text-center">
                <Upload className="w-8 h-8 text-[#0F172A] mx-auto mb-3" strokeWidth={1.8} />
                <h3 className="serif text-2xl text-[#0F172A] mb-2">Importeren</h3>
                <p className="text-sm text-[#64748B] mb-5">Upload de bewerkte Excel terug. Wijzigingen zijn meteen actief op de site (geen restart).</p>
                <label className="cursor-pointer bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl px-5 py-3 inline-flex items-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Excel uploaden
                  <input type="file" accept=".xlsx" hidden disabled={busy}
                    data-testid="import-translations"
                    onChange={(e) => uploadTranslations(e.target.files?.[0])} />
                </label>
              </div>
            </div>
            <div className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl p-5 text-sm text-[#475569]">
              <strong className="text-[#0F172A]">Tips:</strong>
              <ul className="mt-2 space-y-1 list-disc pl-5">
                <li>Bewerk alle talen vrij — ook Spaans is bewerkbaar.</li>
                <li>Laat <code className="bg-[#FFF7ED] px-1 rounded">{`{{placeholders}}`}</code> en emoji's (📍 ❌) ongewijzigd.</li>
                <li>Termen als CUPS, IRPF, IBI, MOVES III blijven hetzelfde in alle talen.</li>
                <li>Lege cellen worden genegeerd (huidige tekst blijft staan).</li>
                <li>Wijzig nooit de kolom <em>Sleutel</em> — anders raakt de tekst de connectie kwijt.</li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
